import clsx from 'clsx';
import React, { useState } from 'react';
import { Target } from '../../models/target';
import { TargetObjectMeta } from '../../models/target-object-meta';
import { TARGET_TYPES, TargetTypesEnum } from '../../models/target-type';
import { NodeLogo } from '@hevoio/hevo-ui-toolkit';
import { HdIcon, HdIconButton, HdTooltip } from '../../../../components';
import { HdDropdownWithExternalTrigger } from '../../../../dropdown/components/dropdown-with-external-trigger/DropdownWithExternalTrigger';
import HdMenuTrigger from '../../../../menu/components/menu-trigger/HdMenuTrigger';
import HdTimeAgo from '../../../../date-time/components/time-ago/HdTimeAgo';
import { getDataIdGenerator } from '../../../../core/utils/generateDataId';
import ActivateTargetAPI from '../../ActivateTargetAPI';
import { TargetConfigPopup } from '../../TargetConfigPopup';
import styles from './styles.module.scss';

const OBJECTS_DROPDOWN_TRIGGER_ID = 'object_explorer_select_object_dropdown_trigger';

export function ObjectExplorerHead({
  objects,
  target,
  selectedTargetObject,
  selectTargetObject,
  lastRefreshedTs,
  needTargetReauth,
  loadingFields
}: {
  objects: TargetObjectMeta[];
  target: Target;
  selectedTargetObject: TargetObjectMeta;
  selectTargetObject: Function;
  lastRefreshedTs: number;
  needTargetReauth: boolean;
  loadingFields: boolean;
}) {
  const [ objectsDropdownOpen, setObjectsDropdownOpen ] = useState(false);

  const selectedTargetObjectName = selectedTargetObject?.displayName;

  const refreshTargetObjects = (e) => {
    e.stopPropagation();
    ActivateTargetAPI.refreshTargetSchema(target.id).then(() => {}, () => {});
  };

  const getTargetObjectDisplayName = (o: any) => {
    const targetObject = o as TargetObjectMeta;

    if (target.targetType === TargetTypesEnum.SALESFORCE && objects.length > 1) {
      return `${ targetObject.displayName } (${ targetObject.apiName })`;
    }

    return targetObject.displayName;
  };

  const dataIdGenerator = getDataIdGenerator('target-object-explorer-head');

  return (
    <div className={clsx(styles.objectExplorerHead, needTargetReauth ? styles.disabled : '')}>
      <HdMenuTrigger
        className='flex-1 overflow-hidden'
        id={OBJECTS_DROPDOWN_TRIGGER_ID}
        dataId={dataIdGenerator('objects-dropdown')}
        onClick={() => setObjectsDropdownOpen(!objectsDropdownOpen)}
        disabled={!!needTargetReauth}
      >
        <div className='center-flex-row'>
          <NodeLogo
            className='mr-3'
            roundedBorders
            logoURL={target.targetTypeLogoUrl}
            darkModeLogoURL={target.targetTypeDarkModeLogoUrl}
            primaryColor={TARGET_TYPES[target.targetType].primaryColor}
            darkModePrimaryColor={TARGET_TYPES[target.targetType].darkModePrimaryColor}
          />

          <div className={styles.objectDetailsContainer}>
            <div className='center-flex-row'>
              <HdTooltip
                title={selectedTargetObject ? selectedTargetObjectName : 'Select Target Object'}
                enableOnTextOverflow
              >
                <span
                  className='text-ellipsis'
                >
                  {selectedTargetObject ? selectedTargetObjectName : 'Select Target Object'}
                </span>
              </HdTooltip>

              {selectedTargetObject ? (
                <TargetConfigPopup
                  target={target}
                  configPopupDisabled={needTargetReauth}
                />
              ) : null}
            </div>

            {selectedTargetObject && !loadingFields ? (
              <div className='center-flex-row'>
                Refreshed:
                <b className='text-nowrap ml-1'>
                  {lastRefreshedTs ? (
                    <HdTimeAgo timestamp={lastRefreshedTs}/>
                  ) : null}
                </b>

                <HdIconButton
                  dataId={dataIdGenerator('refresh')}
                  disabled={needTargetReauth}
                  onClick={refreshTargetObjects}
                >
                  <HdIcon name='refresh' size={1} />
                </HdIconButton>
              </div>
            ) : null}
          </div>
        </div>
      </HdMenuTrigger>

      <HdDropdownWithExternalTrigger
        dataId={dataIdGenerator('')}
        id='object_explorer_select_object_dropdown'
        target={`#${OBJECTS_DROPDOWN_TRIGGER_ID}`}
        selected={selectedTargetObject}
        open={objectsDropdownOpen}
        displayAccessor='displayName'
        valueAccessor='apiName'
        placeholder='Target Object'
        options={objects}
        CustomOption={({ option }) => (
          <>{getTargetObjectDisplayName(option)}</>
        )}
        onChangeEventHandler={o => selectTargetObject(o)}
        onClose={() => {
          setObjectsDropdownOpen(false);
        }}
        group
      />
    </div>
  );
}
